import axios from 'axios';
import { BASE_URL } from './helpers';

export interface ClipEditorMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ClipEditorPatch {
  clip_metadata?: Record<string, any>;
  media_metadata?: Array<{
    media_id: string;
    metadata: Record<string, any>;
  }>;
}

export type ClipEditorIntent = 'text_edit' | 'style_edit' | 'template_creation';

export interface ClipEditorPlan {
  summary?: string;
  steps?: string[];
}

export interface ClipEditorChatRequest {
  context_prompt?: string;
  message: string;
  model?: string;
  history?: ClipEditorMessage[];
}

export interface ClipEditorChatResponse {
  message: string;
  patch: ClipEditorPatch;
  intent?: ClipEditorIntent;
  plan?: ClipEditorPlan;
}

export type MediaMetadataDrafts = Record<string, Record<string, any>>;

const MAX_ERROR_MESSAGE_LENGTH = 360;

const compact = (value: string): string => value.replace(/\s+/g, ' ').trim();

const truncate = (value: string): string => {
  if (value.length <= MAX_ERROR_MESSAGE_LENGTH) return value;
  return `${value.slice(0, MAX_ERROR_MESSAGE_LENGTH - 3)}...`;
};

const parseMaybeJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const extractMessageFromPayload = (payload: unknown): string | null => {
  if (typeof payload === 'string') {
    const parsed = parseMaybeJson(payload);
    if (parsed !== payload) return extractMessageFromPayload(parsed);
    return compact(payload);
  }

  if (!payload || typeof payload !== 'object') return null;

  const record = payload as Record<string, unknown>;
  for (const key of ['message', 'detail', 'error', 'title']) {
    const value = record[key];
    const message = extractMessageFromPayload(value);
    if (message) return message;
  }

  return null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isClipEditorIntent = (value: unknown): value is ClipEditorIntent =>
  value === 'text_edit' || value === 'style_edit' || value === 'template_creation';

const normalizePatch = (value: unknown): ClipEditorPatch => {
  if (!isRecord(value)) return {};

  const patch: ClipEditorPatch = {};
  if (isRecord(value.clip_metadata)) {
    patch.clip_metadata = value.clip_metadata;
  }
  if (Array.isArray(value.media_metadata)) {
    patch.media_metadata = value.media_metadata
      .filter((item): item is { media_id: string; metadata: Record<string, unknown> } =>
        isRecord(item) && typeof item.media_id === 'string' && isRecord(item.metadata)
      )
      .map((item) => ({
        media_id: item.media_id,
        metadata: item.metadata,
      }));
  }

  return patch;
};

const normalizePlan = (value: unknown): ClipEditorPlan | undefined => {
  if (!isRecord(value)) return undefined;
  const plan: ClipEditorPlan = {};
  if (typeof value.summary === 'string') {
    plan.summary = value.summary;
  }
  if (Array.isArray(value.steps)) {
    plan.steps = value.steps.filter((step): step is string => typeof step === 'string');
  }
  return plan.summary || (plan.steps && plan.steps.length > 0) ? plan : undefined;
};

export const normalizeClipEditorChatResponse = (payload: unknown): ClipEditorChatResponse | null => {
  if (!isRecord(payload)) return null;

  const hasKnownResponseField =
    typeof payload.message === 'string' ||
    isRecord(payload.patch) ||
    isClipEditorIntent(payload.intent) ||
    isRecord(payload.plan);

  if (!hasKnownResponseField) return null;

  const response: ClipEditorChatResponse = {
    message: typeof payload.message === 'string' ? payload.message : '',
    patch: normalizePatch(payload.patch),
  };

  if (isClipEditorIntent(payload.intent)) {
    response.intent = payload.intent;
  }

  const plan = normalizePlan(payload.plan);
  if (plan) {
    response.plan = plan;
  }

  return response;
};

export const parseClipEditorChatResponseText = (text: string): ClipEditorChatResponse | null => {
  const jsonBlockRegex = /```json\s*([\s\S]*?)\s*```/;
  const match = text.match(jsonBlockRegex);
  const candidate = match?.[1] || text;

  try {
    return normalizeClipEditorChatResponse(JSON.parse(candidate));
  } catch {
    return null;
  }
};

export const isLikelyClipEditorJsonText = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (/```json/i.test(trimmed)) return true;
  if (!trimmed.startsWith('{')) return false;
  return /"(patch|intent|plan|clip_metadata|media_metadata)"\s*:/.test(trimmed);
};

export const formatClipEditorResponseDisplayText = (response: ClipEditorChatResponse): string => {
  if (response.message.trim()) return response.message.trim();
  if (response.intent === 'template_creation') {
    return response.plan?.summary?.trim() || 'Prepared a style plan.';
  }
  return 'Drafted a safe edit.';
};

export const formatClipEditorStreamingDisplayText = (accumulated: string): string => {
  const parsed = parseClipEditorChatResponseText(accumulated);
  if (parsed) return formatClipEditorResponseDisplayText(parsed);

  const jsonBlockStart = accumulated.search(/```json/i);
  if (jsonBlockStart !== -1) {
    const intro = accumulated.slice(0, jsonBlockStart).trim();
    return intro || 'Preparing a style plan...';
  }

  if (isLikelyClipEditorJsonText(accumulated)) {
    return 'Drafting a safe edit...';
  }

  return accumulated;
};

export const inferClipEditorIntent = (patch?: ClipEditorPatch | null): ClipEditorIntent => {
  const clipMetadata = patch?.clip_metadata || {};
  const hasStylePatch =
    Object.prototype.hasOwnProperty.call(clipMetadata, 'stylePresetDraft') ||
    Object.prototype.hasOwnProperty.call(clipMetadata, 'brandingTag') ||
    Object.prototype.hasOwnProperty.call(clipMetadata, 'stylePresetId') ||
    Object.prototype.hasOwnProperty.call(clipMetadata, 'stylePresetName');

  return hasStylePatch ? 'style_edit' : 'text_edit';
};

export const formatClipEditorErrorMessage = (
  status: number,
  responseText: string,
  model?: string
): string => {
  const parsed = parseMaybeJson(responseText);
  const payloadMessage = extractMessageFromPayload(parsed);
  const message = truncate(payloadMessage || compact(responseText) || 'Unknown error');
  const modelLabel = model?.trim() ? ` Model: ${model.trim()}.` : '';
  const deprecatedHint = model?.trim() === 'x-ai/grok-4-fast'
    ? ' The saved AI editor model is deprecated; use x-ai/grok-4 in Settings.'
    : '';

  return `Clip editor chat failed (${status}).${modelLabel} ${message}${deprecatedHint}`;
};

export const chatClipEditor = (
  clipId: string,
  request: ClipEditorChatRequest
): Promise<ClipEditorChatResponse> =>
  axios.post(`${BASE_URL}/clips/${encodeURIComponent(clipId)}/editor/chat`, request).then((res) => {
    const normalized = normalizeClipEditorChatResponse(res.data);
    return normalized || res.data;
  });

export const applyClipEditorPatch = (
  metadata: Record<string, any>,
  mediaMetadataDrafts: MediaMetadataDrafts,
  patch: ClipEditorPatch
): { metadata: Record<string, any>; mediaMetadataDrafts: MediaMetadataDrafts } => {
  const clipMetadataPatch = { ...(patch.clip_metadata || {}) };
  if (
    clipMetadataPatch.stylePresetDraft &&
    typeof clipMetadataPatch.stylePresetDraft === 'object' &&
    !Array.isArray(clipMetadataPatch.stylePresetDraft)
  ) {
    clipMetadataPatch.stylePresetDraft = {
      ...(metadata.stylePresetDraft && typeof metadata.stylePresetDraft === 'object' ? metadata.stylePresetDraft : {}),
      ...clipMetadataPatch.stylePresetDraft,
    };
  }
  const nextMetadata = {
    ...metadata,
    ...clipMetadataPatch,
  };
  const nextMediaMetadataDrafts: MediaMetadataDrafts = { ...mediaMetadataDrafts };

  (patch.media_metadata || []).forEach((item) => {
    nextMediaMetadataDrafts[item.media_id] = {
      ...(nextMediaMetadataDrafts[item.media_id] || {}),
      ...(item.metadata || {}),
    };
  });

  return {
    metadata: nextMetadata,
    mediaMetadataDrafts: nextMediaMetadataDrafts,
  };
};

export const summarizeClipEditorPatch = (patch: ClipEditorPatch): string[] => [
  ...Object.keys(patch.clip_metadata || {}),
  ...(patch.media_metadata || []).flatMap((item) =>
    Object.keys(item.metadata || {}).map((key) => `${item.media_id}.${key}`)
  ),
];

export const streamChatClipEditor = async function* (
  clipId: string,
  request: ClipEditorChatRequest,
  getAuthHeaders: () => Record<string, string>
): AsyncGenerator<string> {
  const response = await fetch(`${BASE_URL}/clips/${encodeURIComponent(clipId)}/editor/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(formatClipEditorErrorMessage(response.status, errorText, request.model));
  }

  if (!response.body) {
    throw new Error('Clip editor chat failed: empty response body');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const parseLine = (line: string): string | null => {
    if (!line.startsWith('data: ')) return null;
    const data = line.slice(6);
    if (data === '[DONE]') return '[DONE]';

    try {
      const parsed = JSON.parse(data);
      if (parsed.patch || parsed.intent || parsed.plan) {
        return JSON.stringify(parsed);
      }
      return parsed.choices?.[0]?.delta?.content ?? parsed.content ?? parsed.message ?? data;
    } catch {
      return data;
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      const content = parseLine(line);
      if (content === '[DONE]') return;
      if (content) yield content;
    }
  }

  if (buffer) {
    const content = parseLine(buffer);
    if (content && content !== '[DONE]') yield content;
  }
};

const ClipEditorAPI = {
  chatClipEditor,
  streamChatClipEditor,
};

export default ClipEditorAPI;
